import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import useQueryParams from '../../hooks/useQueryParams';
import ResolvePagination from '../Pagination';
import ArrowdownIcon from '../../icons/Arrowdown.icon';
import ChevrondownIcon from '../../icons/Chevrondown.icon';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';

import './Table.styles.scss';

type TableConf<T> = {
  updateInstance: (v: T) => void;
  instance: T;
  updateTableData: React.Dispatch<React.SetStateAction<T[]>>;
  setIsTableLoading?: (b: boolean) => void;
};

export type TableContentTransformations<T> = Partial<
  Record<keyof T, (val: any, config?: TableConf<T>) => React.ReactNode>
>;

type TableProps<T> = {
  className?: string;
  data: Array<T>;
  columns: string[];
  headingTranslations?: Partial<Record<keyof T, string>>;
  tableContentTransformations?: TableContentTransformations<T>;
  sortingValues?: Partial<Record<keyof T, string>>;
  isDataLoading?: boolean;
  totalPages?: number;
  updateTableData?: React.Dispatch<React.SetStateAction<T[]>>;
};

function Table<T extends Record<string, any>>(props: TableProps<T>) {
  const {
    className,
    columns = [],
    data = [],
    headingTranslations,
    tableContentTransformations,
    isDataLoading,
    sortingValues,
    totalPages = 0,
    updateTableData,
  } = props;
  const { t } = useTranslation();
  const [isTableLoading, setIsTableLoading] = useState(false);
  const {
    setQueryParam,
    params: { sort, page = 1 },
  } = useQueryParams<{ sort: string; page: string }>();

  const classes = classNames(className, 'table', {
    loading: isDataLoading || isTableLoading,
  });

  const TableHeading = useMemo(
    () => (
      <thead>
        <tr>
          {columns.map((c) => {
            const sortingVal = sortingValues?.[c];
            const isSelected =
              sortingVal && sort?.includes(sortingVal as string);

            const copy = headingTranslations?.[c]
              ? t(headingTranslations?.[c])
              : (c as string);

            return (
              <th key={c as string} className={c as string}>
                <div
                  className={classNames({
                    selected: isSelected,
                    rotated: sort !== sortingVal,
                  })}
                >
                  {sortingVal ? (
                    <Button
                      onClick={() => {
                        if (sort === sortingVal) {
                          setQueryParam('sort', `-${sortingVal}`);
                        } else {
                          setQueryParam('sort', sortingVal);
                        }
                        setQueryParam('page', 1);
                      }}
                      className={classNames({
                        selected: isSelected,
                        rotated: sort === sortingVal,
                      })}
                    >
                      {copy}
                      {isSelected ? (
                        <ArrowdownIcon className="srt-arr" />
                      ) : (
                        <div className="arw-cnt">
                          <ChevrondownIcon /> <ChevrondownIcon />
                        </div>
                      )}
                    </Button>
                  ) : (
                    copy
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
    ),
    [columns, headingTranslations, setQueryParam, sort, sortingValues, t],
  );

  const TableBody = useMemo(
    () => (
      <tbody>
        {data.map((inst) => (
          <tr key={inst._id}>
            {columns.map((c) => (
              <td key={c as string}>
                {tableContentTransformations?.[c as keyof T]?.(inst[c], {
                  instance: inst,
                  updateInstance: (newVal: T) =>
                    updateTableData((old) =>
                      old.map((e) => (e._id === inst._id ? newVal : e)),
                    ),
                  updateTableData,
                  setIsTableLoading,
                }) || inst[c]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    ),
    [columns, data, tableContentTransformations, updateTableData],
  );

  return (
    <div className={classes}>
      <table>
        {TableHeading}
        {TableBody}
      </table>
      <ResolvePagination
        currentPage={+page}
        lastPage={totalPages}
        setPage={(num) => setQueryParam('page', num)}
      />
    </div>
  );
}

export default Table;
